import React, { FC, useCallback, useEffect, useState } from "react";
import "./style.scss";
import GoBack from "../../../assets/GoBackApp.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { PATH_LIST } from "shared/lib/react-router";
import ReplenishmentDetails from "../components/replenishment-details";
import Currency from "../components/deposit-currency";
import { CurrencyModal } from "../components/currency-modal";
import Button from "shared/ui/button";
import { useBlockScrollbar } from "hooks/useBlockScrollbar";
import Modal from "widgets/auth/modal";
import { accountStore, useWithdrawalInfo } from "entities/account";
import { useStore } from "zustand";
import Loading from "widgets/loading";
import { CURRENCY_FLAGS } from "static/currencies";
import {
  sessionStore,
  useUserContract,
  useUserName,
} from "entities/session/model/sessionModel";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { dashboardStore } from "entities/dashboard";
import WithdrawalInput from "../components/withdrawal-input";
import { EntityType } from "static/types";
import Russia from "../../../assets/currencies/RUB.svg";
import Other from "../../../assets/ForeignCountries.svg";
import { userVerified } from "helpers/functions";
import { useVerificationNavigation } from "hooks/useNavigateVerification";
import { sircapApi } from "shared/api/sircap";
import type {
  IWithdrawalRequisites,
  WithdrawalBankType,
} from "shared/api/sircap/Api";

export const WithdrawalPage: FC = () => {
  const navigate = useNavigate();
  const [currencyModal, setCurrencyModal] = useState<
    null | "bank" | "currency"
  >(null);
  const [modalWithdrawal, setModalWithdrawal] = useState(false);
  const [continueVerificationModal, setContinueVerificationModal] =
    useState(false);
  const { navigateVerification } = useVerificationNavigation();
  const userName = useUserName();
  const contract = useUserContract();

  const { t } = useTranslation("app");
  const { t: o } = useTranslation("onboarding-auth");
  const { t: err } = useTranslation("errors");

  const {
    isLoading,
    requestWithdrawal,
    selectedWithdrawCurrency,
    selectCurrency,
    withdrawInfo: accounts,
    getWithdrawInfo,
    kyc,
    kyb,
  } = useStore(accountStore);

  const { user, entity } = useStore(sessionStore);

  const [selectedBank, setSelectedBank] = useState<number | string | null>(
    null
  );

  const [formData, setFormData] = useState({
    theFullName: userName || "",
    address:
      (entity === EntityType.Individual
        ? kyc?.address_json?.street
        : kyb?.personal_address) || "",
    city: "",
    country: "",
    // IBAN for a foreign bank
    accountNumber: "",
    bankName: "",
    BIC: "",
    tin: "",
    kpp: "",
    SWIFT: "",
    withdrawalAmount: 0,
  });

  // what the client typed in for Russian and foreign banks before, newest first
  const [requisites, setRequisites] = useState<IWithdrawalRequisites[]>([]);
  useEffect(() => {
    sircapApi.transfer
      .withdrawRequisites()
      .then((resp: any) => {
        if (!resp.error) setRequisites(resp.data?.data || []);
      })
      .catch(() => undefined);
  }, []);

  const updateFormField = (
    field: keyof typeof formData,
    value: string | number
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const withdrawInfo = useWithdrawalInfo(
    selectedWithdrawCurrency,
    selectedBank
  );
  const currencies = new Set(accounts?.map((v) => v.currency.code));

  const handleNavigate = () => {
    if (isLoading) return;
    navigate(PATH_LIST.account);
  };

  useEffect(() => {
    getWithdrawInfo();
    // loadUserAccounts();
  }, []);

  useEffect(() => {
    if (!userVerified()) {
      setContinueVerificationModal(true);
    }
  }, []);

  useEffect(() => {
    const banks = new Set(accounts.map((a) => a.info_source?.id));
    setSelectedBank(Array.from(banks)[0] || null);
  }, [accounts]);

  useEffect(() => {
    updateFormField("accountNumber", withdrawInfo?.account_number || "");
  }, [withdrawInfo]);

  const getUserAccount = useCallback(() => {
    // const acc = user_deposit_accounts.find(
    //   (acc) => acc.currency.code === selectedWithdrawCurrency
    // );
    const acc = accounts.find(
      (acc) => acc.currency.code === selectedWithdrawCurrency
    );
    if (acc) {
      return `${+acc.amount.toFixed(2)} ${acc.currency.symbol}`;
    } else {
      return "0";
    }
  }, [accounts, selectedWithdrawCurrency]);

  const getUserAccountAmount = useCallback(() => {
    // const acc = user_deposit_accounts.find(
    //   (acc) => acc.currency.code === selectedWithdrawCurrency
    // );
    const acc = accounts.find(
      (acc) => acc.currency.code === selectedWithdrawCurrency
    );
    if (acc) {
      return acc.amount;
    } else {
      return 0;
    }
  }, [accounts, selectedWithdrawCurrency]);

  useBlockScrollbar(!!currencyModal, [currencyModal]);

  // A Russian or a foreign bank is not on our list: the client types the
  // details in, and the ones typed last time come back.
  const bankType: WithdrawalBankType | null =
    selectedBank === "Russia"
      ? "russia"
      : selectedBank === "Other"
      ? "foreign"
      : null;
  const isCompany = entity === EntityType.Entity;

  const saved = requisites.filter((r) => r.bank_type === bankType);
  const savedNames = Array.from(
    new Set(saved.map((r) => r.data.theFullName).filter(Boolean))
  );
  // the person behind a saved row; the account is not part of it
  const personOf = (r: IWithdrawalRequisites) => ({
    theFullName: r.data.theFullName || "",
    address: r.data.address || "",
    city: r.data.city || "",
    country: r.data.country || "",
    tin: r.data.tin || "",
    kpp: r.data.kpp || "",
  });

  // Choosing the kind of bank or the currency fills the form: the person from
  // the newest row of that kind, the account only from the row of this
  // currency - an EUR IBAN must not go out with a USD withdrawal.
  useEffect(() => {
    if (!bankType) return;
    const forCurrency = saved.find(
      (r) => r.currency === selectedWithdrawCurrency
    );
    const person = forCurrency || saved[0];
    setFormData((prev) => ({
      ...prev,
      ...(person ? personOf(person) : {}),
      accountNumber: forCurrency?.account_number || "",
      bankName: forCurrency?.data.bankName || "",
      BIC: forCurrency?.data.BIC || "",
      SWIFT: forCurrency?.data.SWIFT || "",
    }));
  }, [bankType, selectedWithdrawCurrency, requisites]);

  // picking a name used before brings its address, city and country with it
  const pickName = (value: string) => {
    const match = saved.find((r) => r.data.theFullName === value);
    setFormData((prev) => ({
      ...prev,
      ...(match ? personOf(match) : {}),
      theFullName: value,
    }));
  };

  type TextKey = Exclude<keyof typeof formData, "withdrawalAmount">;
  const text = (field: TextKey, label: string) => ({
    type: "text" as const,
    field,
    label,
    value: formData[field],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      field === "theFullName" && bankType
        ? pickName(String(e.target.value))
        : updateFormField(field, String(e.target.value)),
    suggestions: field === "theFullName" && bankType ? savedNames : undefined,
  });
  const amountField = (label: string) => ({
    type: "number" as const,
    label,
    value: formData.withdrawalAmount || 0,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      updateFormField("withdrawalAmount", +String(e.target.value) || 0),
    max: getUserAccountAmount(),
    span: getUserAccount(),
  });

  const details: Array<
    ReturnType<typeof text> | ReturnType<typeof amountField>
  > =
    bankType === "russia"
      ? isCompany
        ? [
            text("theFullName", o("companyName")),
            text("address", t("address")),
            text("tin", t("tin")),
            text("kpp", t("kpp")),
            text("accountNumber", t("clientAccountNumber")),
            text("bankName", t("bankName")),
            text("BIC", t("BIC")),
            amountField(t("amountIn", { currency: selectedWithdrawCurrency })),
          ]
        : [
            text("theFullName", t("fullNameWithPatronymic")),
            text("address", t("clientAddress")),
            text("accountNumber", t("clientAccountNumber")),
            text("bankName", t("bankName")),
            text("BIC", t("BIC")),
            amountField(t("amountIn", { currency: selectedWithdrawCurrency })),
          ]
      : bankType === "foreign"
      ? [
          text("theFullName", t("fullName")),
          text("address", t("beneficiaryAddress")),
          text("city", t("city")),
          text("country", t("country")),
          text("accountNumber", t("ibanAccountNumber")),
          text("SWIFT", t("swiftCode")),
          amountField(t("withdrawalAmount")),
        ]
      : [
          text("accountNumber", t("accountNumber")),
          amountField(t("withdrawalAmount")),
        ];

  // every field on screen is required
  const textFields = details.filter(
    (d): d is ReturnType<typeof text> => d.type === "text"
  );
  const isFormValid = () =>
    textFields.every((d) => String(d.value).trim()) &&
    formData.withdrawalAmount > 0 &&
    formData.withdrawalAmount <= getUserAccountAmount();

  const handleWithdrawal = () => {
    if (!userVerified()) {
      setContinueVerificationModal(true);
      return;
    }

    // Additional validation before submission
    if (!isFormValid()) {
      toast.error(t("pleaseFillAllRequiredFields"));
      return;
    }

    if (isNaN(formData.withdrawalAmount as number)) {
      toast.error(t("invalidAmount"));
      return;
    }

    const amount = +(formData.withdrawalAmount || 0);

    // The account number travels in its own field; the rest of what is on
    // screen is the request's details.
    const formDataToSend = Object.fromEntries(
      textFields
        .filter((d) => d.field !== "accountNumber")
        .map((d) => [d.field, String(d.value).trim()])
    );

    const data = {
      amount,
      currency: selectedWithdrawCurrency,
      account_number: formData.accountNumber.trim(),
      info_source: bankType ? null : (selectedBank as number),
      data: formDataToSend,
      ...(bankType ? { bank_type: bankType } : {}),
    };

    requestWithdrawal(data, (resp: any) => {
      if (resp.error) {
        toast.error(err(String(resp.error.code)));
      } else {
        setModalWithdrawal(true);
      }
    });
  };

  if (
    (!withdrawInfo && selectedBank !== "Other" && selectedBank !== "Russia") ||
    isLoading
  ) {
    return (
      <div className="withdraw deposit">
        <div className="deposit-header">
          <img
            className="go-back-portfolio"
            onClick={handleNavigate}
            src={GoBack}
            alt="Go Back"
          />
          <p>{t("withdrawal")}</p>
        </div>

        {isLoading ? (
          <Loading />
        ) : (
          <div className="not-found-coming-soon">
            <p>{t("noData")}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="withdraw deposit">
      <div className="deposit-header">
        <img
          className="go-back-portfolio"
          onClick={handleNavigate}
          src={GoBack}
          alt="Go Back"
        />
        <p>{t("withdrawal")}</p>
      </div>

      <div className="deposit-main">
        <Currency
          image={CURRENCY_FLAGS[selectedWithdrawCurrency]}
          currency={selectedWithdrawCurrency}
          onClick={() => setCurrencyModal("currency")}
        />
        <Currency
          image={
            withdrawInfo
              ? withdrawInfo.info_source.image
              : selectedBank === "Other"
              ? Other
              : Russia
          }
          onClick={() => setCurrencyModal("bank")}
          bank={
            withdrawInfo
              ? withdrawInfo.info_source.name
              : selectedBank === "Other"
              ? t("withdrawToForeignBanks")
              : t("withdrawToRussianBanks")
          }
        />

        {/* <ReplenishmentDetails
          nameOfDetail={t("fullName")}
          detail={userName || ""}
        /> 
        */}
        {/* <ReplenishmentDetails
          nameOfDetail={t("accountNumber")}
          detail={withdrawInfo.account_number || ""}
        /> */}

        {details.map((detail: any) => (
          <WithdrawalInput
            key={detail.label}
            type={detail.type}
            label={detail.label}
            value={detail.value}
            onChange={detail.onChange}
            max={detail.max}
            span={detail.span}
            suggestions={detail.suggestions}
          />
        ))}
      </div>

      <div className="btn-wrapper">
        <Button
          children={t("withdraw")}
          variant="dark"
          onClick={handleWithdrawal}
          disabled={!isFormValid() || isLoading}
        />
      </div>

      {currencyModal && (
        <CurrencyModal
          type={currencyModal}
          withdraw
          onClose={() => setCurrencyModal(null)}
          onCurrencyChange={
            currencyModal === "bank"
              ? setSelectedBank
              : (v) => {
                  // setSelectedBank(null);
                  // if (selectedBank !== "Other" && selectedBank !== "Russia") {
                  //   setSelectedBank(null);
                  // }
                  selectCurrency("withdraw")(v);
                }
          }
          selectedCurrency={selectedWithdrawCurrency}
          selectedBank={selectedBank}
          currencies={Array.from(currencies)}
          banks={
            currencyModal === "bank"
              ? accounts
                  .filter((d) => d.currency.code === selectedWithdrawCurrency)
                  .map((d) => d.info_source)
              : undefined
          }
        />
      )}

      {modalWithdrawal && (
        <Modal
          customModal="modalWithdrawal"
          onClick={() => {
            setModalWithdrawal(false);
            navigate(PATH_LIST.account);
          }}
          onHide={() => {
            setModalWithdrawal(false);
            navigate(PATH_LIST.account);
          }}
          title={t("withdrawalProcessing")}
          text={t("requestBeingProcessed")}
          btnText={t("ok")}
          showSecondBtn={false}
          customOutlet="customOutlet customWithdrawalOutlet"
        />
      )}

      {continueVerificationModal && (
        <Modal
          customModal="modalLogOut welcomeModal"
          onClick={() => {
            window.history.replaceState({}, "");
            navigateVerification();
          }}
          onHide={() => {
            window.history.replaceState({}, "");
            setContinueVerificationModal(false);
            navigate(PATH_LIST.account);
          }}
          title={t("completeVerification")}
          text={t("toFullyActivateVerification")}
          btnText={t("completeVerificationBtn")}
          showSecondBtn={false}
          customOutlet="customOutlet"
        />
      )}
    </div>
  );
};
